// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld-base';
import L from 'leaflet';
import traceService from './tracePluginService';
import '../../uiComponents/index';
import './traceOutPane';
import './findRoutePane';

// Provides GUI for tracing from current feature
myw.TracePlugin = myw.Plugin.extend({
    buttons: {
        dialog: myw.PluginButton.extend({
            titleMsg: 'toolbar_msg',
            imgSrc: 'images/toolbar/network-trace.svg',
            action() {
                this.app.recordFunctionalityAccess('core.toolbar.network_trace');
                this.owner.toggleDialog();
            }
        })
    },

    state: {},

    /**
     * @class Plugin with a dialog to run network traces
     * Provides a 'dialog' button to open the dialog
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);
        this.owner = owner;
        this.database = this.app.database;
        const self = this;
        this.resetState();

        this.traceOutPanel = new myw.TraceOutPane({
            app: this.app,
            owner: this.owner
        });

        const findRoutePanel = new myw.TraceFindRoutePane({
            name: 'tracePanel',
            app: this.app,
            owner: this.owner
        });

        this.tabPanel = new myw.TabPanel({
            app: this.app,
            selected: 0,
            tabs: [
                { title: this.msg('trace_out_tab_title'), pane: this.traceOutPanel },
                { title: this.msg('find_route_tab_title'), pane: findRoutePanel }
            ]
        });

        this.dialog = new myw.Dialog({
            title: this.msg('modal_title'),
            dialogClass: 'networkTraceModal',
            contents: this.tabPanel.$el,
            modal: false,
            buttons: null,
            autoOpen: false,
            minWidth: 450,
            minHeight: 295
        });
        this.dialog.render();

        this.dialog.$el.on('dialogclose', () => {
            self.owner.fire('tracePlugin-close');
            self._clearMarkers();
            self.resetState();
        });

        this._registerEvents();
    },

    resetState() {
        this.state = {
            active: false,
            activeTab: 0,
            selectedFeatures: {
                from: null,
                to: null
            },
            availableNetworks: [],
            availableDirections: [],
            availableSubpaths: [],
            network: null
        };
    },

    toggleDialog() {
        const d = this.dialog.$el;
        const isOpen = d.dialog('isOpen');

        if (isOpen) {
            d.dialog('close');
            return;
        }

        d.dialog('open');
        this.state.active = true;

        myw.geometry.init().then(() => {
            const feature = this.app.currentFeature;
            if (feature) {
                this.owner.fire('tracePlugin-setFromFeature', { feature: feature });
            }
        });
    },
    /*
     * Updates internal state and dispatches new state information to tab panels
     */
    _triggerRender() {
        const state = this.state;
        const owner = this.owner;
        owner.fire('tracePlugin-stateChange', state);
    },

    _registerEvents() {
        const owner = this.owner;
        const app = this.app;
        const self = this;

        app.on('currentFeature-changed', args => {
            self._triggerRender(self.state);
        });

        app.on('tabPanel-activeTab', tab => {
            self.state.activeTab = tab.id;
            self._clearMarkers();
            self._displayMarkers();
        });

        owner.on('tracePlugin-setFromFeature', args => {
            const feature = args.feature;
            self.state.selectedFeatures.from = feature;
            self._displayFromMarker();
            self._getNetworkAndDirection(feature);
        });

        owner.on('tracePlugin-setToFeature', args => {
            const feature = args.feature;
            self.state.selectedFeatures.to = feature;
            self._displayToMarker();
            self._triggerRender(self.state);
        });
    },

    _displayMarkers() {
        switch (this.state.activeTab) {
            case 0:
                this._displayFromMarker();
                break;
            default:
                this._displayFromMarker();
                this._displayToMarker();
        }
    },

    _getNetworkAndDirection(feature) {
        const state = this.state;
        const self = this;
        traceService
            .getFeatureNetwork(feature)
            .then(response => {
                state.networks = response.networks;
                state.availableNetworks = Object.keys(response.networks)
                    .map(k => ({
                        id: k,
                        label: this.app.localise(response.networks[k].external_name, k)
                    }))
                    .sort((a, b) => {
                        if (a.label < b.label) return -1;
                        if (a.label > b.label) return 1;
                        return 0;
                    });
                state.network = response.selectedNetwork;
                state.availableDirections = [];
                self._triggerRender(state);
            })
            .catch(e => {
                console.log('error', e);
                state.networks = {};
                state.availableNetworks = [];
                state.availableDirections = [];
                state.network = null;
                self._triggerRender(state);
            });
    },

    _displayFromMarker() {
        const state = this.state;
        const feature = state.selectedFeatures.from;

        if (state.active && feature) {
            if (state.fromMarker) {
                state.fromMarker.remove();
            }
            const m = this._renderMarker(feature, 'From', 'green');
            state.fromMarker = m;
        }
    },

    _displayToMarker() {
        const state = this.state;
        const feature = state.selectedFeatures.to;

        if (state.active && feature) {
            if (state.toMarker) state.toMarker.remove();
            this.state.toMarker = this._renderMarker(feature, 'To', 'red');
        }
    },

    _renderMarker(feature, label, marker) {
        const app = this.app;
        const geom = feature.getGeometry();

        if (geom) {
            let markerPosition = feature.getGeometry().coordinates;
            if (geom.type == 'LineString') {
                const length = geom.length() / 2;
                markerPosition = geom.pointAtDistance(length).coordinates;
            }
            if (geom.type == 'Polygon') {
                markerPosition = feature.getGeometry().coordinates[0][0];
            }
            const icon = L.icon({
                iconUrl: `images/markers/${marker}.png`,
                iconSize: [32, 32],
                iconAnchor: [17, 32]
            });

            return L.marker([markerPosition[1], markerPosition[0]], { icon: icon })
                .addTo(app.map)
                .bindTooltip(label, {
                    permanent: false,
                    direction: 'right',
                    sticky: false,
                    offset: [14, -32]
                });
        }
    },

    _clearMarkers() {
        const state = this.state;
        if (state.fromMarker) state.fromMarker.remove();
        if (state.toMarker) state.toMarker.remove();
    }
});
